import { useState, useEffect } from 'react';
import { supabase } from '../../supabase/client';

interface User {
  id: string;
  email: string;
  plan_type: string;
  quota: number;
}

interface QuotaAdjustmentModalProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type AdjustmentType = 'credit_add' | 'credit_subtract' | 'credit_set' | 'plan_change' | 'quota_reset';

export default function QuotaAdjustmentModal({ user, isOpen, onClose, onSuccess }: QuotaAdjustmentModalProps) {
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>('credit_add');
  const [amount, setAmount] = useState('');
  const [newPlanType, setNewPlanType] = useState('junior_sdr');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setNewPlanType(user.plan_type);
      setReason('');
      setAmount('');
      setError(null);
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const calculateNewQuota = (): number => {
    const currentQuota = user.quota;
    const adjustmentAmount = parseInt(amount) || 0;
    
    switch (adjustmentType) {
      case 'credit_add':
        return currentQuota + adjustmentAmount;
      case 'credit_subtract':
        return Math.max(0, currentQuota - adjustmentAmount);
      case 'credit_set':
        return adjustmentAmount;
      case 'plan_change':
        return currentQuota; // Plan change doesn't affect quota - both plans start with 0
      case 'quota_reset':
        return 0; // Both plans have 0 default credits - must purchase separately
      default:
        return currentQuota;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate inputs
      if (!reason.trim()) {
        throw new Error('Reason is required');
      }

      if (['credit_add', 'credit_subtract', 'credit_set'].includes(adjustmentType) && !amount) {
        throw new Error('Amount is required for credit adjustments');
      }

      // Call the adjust_user_quota function
      const { data, error: rpcError } = await supabase.rpc('adjust_user_quota', {
        target_user_id: user.id,
        adjustment_type: adjustmentType,
        adjustment_amount: ['credit_add', 'credit_subtract', 'credit_set'].includes(adjustmentType) 
          ? parseInt(amount) : null,
        new_plan_type: adjustmentType === 'plan_change' ? newPlanType : null,
        reason: reason.trim()
      });

      if (rpcError) {
        // If RPC function doesn't exist, show a preview message
        if (rpcError.message.includes('function') && rpcError.message.includes('does not exist')) {
          setError('Database schema not deployed. This would adjust the user quota in production.');
          
          // Simulate success after 2 seconds for preview
          setTimeout(() => {
            onSuccess();
            onClose();
          }, 2000);
          return;
        }
        throw rpcError;
      }

      if (data && data.length > 0 && !data[0].success) {
        throw new Error(data[0].message || 'Failed to adjust quota');
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      const error = err as Error;
      console.error('Error adjusting quota:', error);
      setError(error.message || 'Failed to adjust quota');
    } finally {
      setLoading(false);
    }
  };

  const newQuota = calculateNewQuota();

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Adjust Quota - {user.email}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Current Status */}
          <div className="mb-4 p-3 bg-gray-50 rounded-md">
            <div className="text-sm text-gray-600">Current Status</div>
            <div className="text-lg font-medium">
              {user.quota} credits • {user.plan_type === 'top_qualifier_sdr' ? 'Top Qualifier' : 'Junior SDR'}
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Adjustment Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Action
              </label>
              <select
                value={adjustmentType}
                onChange={(e) => setAdjustmentType(e.target.value as AdjustmentType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="credit_add">Add Credits</option>
                <option value="credit_subtract">Subtract Credits</option>
                <option value="credit_set">Set Credits</option>
                <option value="plan_change">Change Plan Type</option>
                <option value="quota_reset">Reset to Zero</option>
              </select>
            </div>

            {/* Amount Input */}
            {['credit_add', 'credit_subtract', 'credit_set'].includes(adjustmentType) && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {adjustmentType === 'credit_set' ? 'New Quota' : 'Amount'}
                </label>
                <input
                  type="number"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter amount"
                  required
                />
              </div>
            )}

            {/* Plan Type Selection */}
            {adjustmentType === 'plan_change' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Plan Type
                </label>
                <select
                  value={newPlanType}
                  onChange={(e) => setNewPlanType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="junior_sdr">Junior SDR</option>
                  <option value="top_qualifier_sdr">Top Qualifier</option>
                </select>
              </div>
            )}

            {/* Reason */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Explain the reason for this adjustment..."
                required
              />
            </div>

            {/* Preview */}
            <div className="mb-4 p-3 bg-blue-50 rounded-md">
              <div className="text-sm text-blue-600">Preview</div>
              <div className="text-lg font-medium text-blue-900">
                New Quota: {newQuota} credits
              </div>
              {adjustmentType === 'plan_change' && (
                <div className="text-sm text-blue-700">
                  Plan: {newPlanType === 'top_qualifier_sdr' ? 'Top Qualifier' : 'Junior SDR'}
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="text-sm text-red-600">{error}</div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Applying...</span>
                  </>
                ) : (
                  <span>Apply Changes</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}